import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Package } from 'lucide-react';
import api from '../api';

export default function PricingSchemes() {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', key: '', description: '', markup_percent: 0, is_cost: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.request('/api/pricing-schemes');
      if (data.length === 0) {
        await api.request('/api/pricing-schemes/seed-defaults', { method: 'POST' });
        const seeded = await api.request('/api/pricing-schemes');
        setSchemes(seeded);
      } else {
        setSchemes(data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: '', key: '', description: '', markup_percent: 0, is_cost: false });
    setShowForm(true);
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, key: s.key, description: s.description || '', markup_percent: s.markup_percent || 0, is_cost: s.is_cost || false });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Scheme name is required'); return; }
    if (!form.key.trim()) { alert('Scheme key is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.request(`/api/pricing-schemes/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await api.request('/api/pricing-schemes', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing scheme?')) return;
    try {
      await api.request(`/api/pricing-schemes/${id}`, { method: 'DELETE' });
      load();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const handleApplyAll = async (s) => {
    const desc = s.is_cost ? 'sell at cost (no markup)' : `${s.markup_percent}% markup`;
    if (!window.confirm(`Apply "${s.name}" (${desc}) to ALL products in the store?\n\nThis will recompute the ${s.key} price for every product based on its cost.`)) return;
    try {
      const res = await api.request(`/api/pricing-schemes/${s.id}/apply-to-all`, { method: 'POST' });
      alert(`Updated pricing on ${res.updated} products.`);
      load();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const handleRecomputeAll = async () => {
    if (!window.confirm('Recompute prices for EVERY product across EVERY scheme?\n\nThis takes a moment and rewrites Regular, PWO, and any custom scheme prices from each product\'s current cost.')) return;
    try {
      const res = await api.request('/api/pricing-schemes/recompute-all-prices', { method: 'POST' });
      const summary = (res.schemes_applied || []).map(s => `${s.scheme}: ${s.updated}`).join('\n');
      alert(`Done.\n\n${summary}`);
      load();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="pricing-schemes-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Pricing Schemes</h2>
          <p className="text-muted-foreground mt-1">Each scheme defines a markup over cost. Editing a scheme — or clicking "Apply to All" — recomputes that scheme's price field on every product in the store.</p>
        </div>
        <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="new-scheme-btn">
          <Plus className="h-4 w-4" /> New Scheme
        </button>
      </div>

      {/* Global recompute action — runs every scheme over every product */}
      <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <div className="text-sm">
          <div className="font-semibold text-amber-900">Recompute All Product Prices</div>
          <div className="text-amber-800 mt-0.5">After editing scheme markups (or in bulk), refresh every product's Regular / PWO / custom-scheme prices from their current cost.</div>
        </div>
        <button onClick={handleRecomputeAll} className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 whitespace-nowrap" data-testid="recompute-all-prices-btn">
          Recompute All Prices
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="scheme-form">
          <h3 className="font-semibold">{editingId ? 'Edit Pricing Scheme' : 'New Pricing Scheme'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="e.g. Regular Markup" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="scheme-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Key * (no spaces)</label>
              <input type="text" value={form.key} onChange={e => setForm({...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                placeholder="e.g. regular" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="scheme-key" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
              <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Markup % (over cost)</label>
              <input type="number" step="0.1" value={form.markup_percent} onChange={e => setForm({...form, markup_percent: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="scheme-markup" disabled={form.is_cost} />
            </div>
            <div className="flex items-center gap-3 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_cost} onChange={e => setForm({...form, is_cost: e.target.checked, markup_percent: e.target.checked ? 0 : form.markup_percent})}
                  className="rounded" data-testid="scheme-at-cost" />
                <span className="text-sm font-medium">Sell at cost (no markup)</span>
              </label>
            </div>
          </div>
          {editingId && (
            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">⚠️ Saving will immediately recompute prices for ALL products in the store using this scheme's formula (cost × markup).</p>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-scheme-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border rounded-md hover:bg-muted">
              <X className="h-4 w-4 inline mr-1" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Schemes List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Scheme</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Key</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Pricing</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Products</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : schemes.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">No pricing schemes</td></tr>
            ) : schemes.map(s => (
              <tr key={s.id} className="hover:bg-muted/20">
                <td className="px-4 py-3">
                  <p className="font-medium">{s.name}</p>
                  {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                </td>
                <td className="px-4 py-3"><code className="px-1.5 py-0.5 bg-muted rounded text-xs">{s.key}</code></td>
                <td className="px-4 py-3">
                  {s.is_cost ? (
                    <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-amber-100 text-amber-700">At Cost</span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 text-blue-700">{s.markup_percent}% markup</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="flex items-center justify-end gap-1 text-muted-foreground">
                    <Package className="h-3.5 w-3.5" /> {s.product_count}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleApplyAll(s)} className="px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium" title="Recompute prices for ALL products" data-testid={`apply-scheme-${s.id}`}>
                      Apply to All
                    </button>
                    <button onClick={() => handleEdit(s)} className="p-1.5 hover:bg-muted rounded-md" data-testid={`edit-scheme-${s.id}`}>
                      <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-scheme-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
